import { FC, ReactElement, useState, useEffect } from 'react'
import React from 'react'
import styled from 'styled-components'
import { WeatherRain } from '@styled-icons/fluentui-system-regular/WeatherRain'
import { Temperature } from '@styled-icons/fluentui-system-regular/Temperature'
import { WeatherSunny } from '@styled-icons/fluentui-system-regular/WeatherSunny'
import { WeatherSunnyLow } from '@styled-icons/fluentui-system-regular/WeatherSunnyLow'
import {
  prettifyNumber,
  getExtendedName,
  getDate,
  getMonth,
  getSentence,
  getEvaluationRain,
  evaluateRatio,
  evaluateExtremeRatio,
  dayReached,
} from './Words'

const tropicalMeans: Record<
  string,
  { tropical_night_mean: number; tropical_day_mean: number }
> = {
  PUY: { tropical_night_mean: 6.1, tropical_day_mean: 6.8 },
  GVE: { tropical_night_mean: 0.8, tropical_day_mean: 18.1 },
  CGI: { tropical_night_mean: 0.6, tropical_day_mean: 10.0 },
  PAY: { tropical_night_mean: 0.1, tropical_day_mean: 11.3 },
  CHD: { tropical_night_mean: 0.0, tropical_day_mean: 3.0 },
  SMA: { tropical_night_mean: 1.1, tropical_day_mean: 8.5 },
  BAS: { tropical_night_mean: 1.5, tropical_day_mean: 14.3 },
  BER: { tropical_night_mean: 0.0, tropical_day_mean: 9.4 },
  LUZ: { tropical_night_mean: 0.5, tropical_day_mean: 9.4 },
  STG: { tropical_night_mean: 1.8, tropical_day_mean: 1.3 },
  LUG: { tropical_night_mean: 15.7, tropical_day_mean: 11.7 },
}

// Show tropical days and nights from June to December
const showTropical = new Date().getMonth() >= 5

const Facts: FC<FactsProps> = ({
  station = 'PUY',
  stationName = 'Pully',
  lang = 'fr',
}): ReactElement => {
  const [tempFacts, setTempFacts] = useState<Temp | null>(null)
  const [rainFacts, setRainFacts] = useState<Rain | null>(null)
  const [percFacts, setPercFacts] = useState<PercentageGroup>({})
  const [ready, setReady] = useState(false)
  const iconSize = 48

  const askForScroll = (chartKey: string) => {
    console.log(chartKey)
    window.scrollTarget = chartKey
    window.dispatchEvent(new Event('scrollToElement'))
  }

  useEffect(() => {
    fetch(
      `https://storage.googleapis.com/wch-interactive/weather-dashboard/${station}-temperature.json?nocache=${new Date().getTime()}`
    )
      .then((res) => res.json())
      .then(setTempFacts)
    fetch(
      `https://storage.googleapis.com/wch-interactive/weather-dashboard/${station}-percentage-group.json?nocache=${new Date().getTime()}`
    )
      .then((res) => res.json())
      .then(setPercFacts)
    fetch(
      `https://storage.googleapis.com/wch-interactive/weather-dashboard/${station}-rain.json?nocache=${new Date().getTime()}`
    )
      .then((res) => res.json())
      .then(setRainFacts)
  }, [station])

  useEffect(() => {
    if (tempFacts && rainFacts && percFacts) {
      setReady(true)
    }
  }, [tempFacts, rainFacts, percFacts])

  return (
    <>
      <p>
        {lang === 'de' &&
          tempFacts &&
          'Datenstand: ' + getDate(tempFacts.current_ts.toString(), 'de')}
      </p>
      <BoxContainer>
        {ready && (
          <>
            {/* ====== 1. Temperature ====== */}
            <ValueBox color='var(--orange-main)'>
              <h3>
                <Anchor
                  onClick={() => {
                    askForScroll('day')
                  }}
                >
                  <Temperature size={iconSize} />
                  {lang === 'fr' ? 'Température' : 'Temperatur'}
                </Anchor>
              </h3>
              <p className='statement'>
                {getSentence(
                  tempFacts!.days_above_norm.count,
                  'temperature',
                  lang
                )}
              </p>
              <p>
                {lang === 'fr'
                  ? ((percFacts.category as unknown as string) !== 'middle 50%'
                      ? evaluateExtremeRatio(percFacts, 'fr')
                      : '') +
                    'La température moyenne de ' +
                    tempFacts!.days_above_norm.count +
                    ' des 100 derniers jours a dépassé la norme à ' +
                    getExtendedName(station, stationName, 'fr') +
                    '. '
                  : 'Die Durchschnittstemperatur von ' +
                    tempFacts!.days_above_norm.count +
                    ' der vergangenen 100 Tage lag in ' +
                    stationName +
                    ' über der Norm.'}
              </p>
            </ValueBox>
            {/* ====== 2. Rain ====== */}
            <ValueBox color='var(--blue-main)'>
              <h3>
                <Anchor
                  onClick={() => {
                    askForScroll('monthRain')
                  }}
                >
                  <WeatherRain size={iconSize} />
                  {lang === 'fr' ? 'Précipitations' : 'Niederschlag'}
                </Anchor>
              </h3>
              <p className='statement'>
                {getSentence(+rainFacts!.ratio, 'rain', lang)}
              </p>
              <p>
                {lang === 'fr'
                  ? 'Durant les trois derniers mois, il a ' +
                    getEvaluationRain(+rainFacts!.ratio, lang) +
                    ' plu que la norme'
                  : 'In den letzten drei Monaten gab es ' +
                    getEvaluationRain(+rainFacts!.ratio, lang) +
                    ' Niederschlag als normal'}
                {evaluateRatio(+rainFacts!.ratio) !== 0 && lang === 'fr'
                  ? ', en particulier en ' +
                    getMonth(+rainFacts!.extreme_month, true, lang) +
                    ' : ' +
                    rainFacts!.extreme_month_ratio_percentage +
                    '% de précipitations en ' +
                    (+rainFacts!.ratio < 1 ? 'moins' : 'plus') +
                    ' que la norme pour ce mois'
                  : evaluateRatio(+rainFacts!.ratio) !== 0 &&
                    ', besonders im ' +
                      getMonth(+rainFacts!.extreme_month, false, lang)}
                {evaluateRatio(+rainFacts!.ratio) === 0 && lang === 'fr'
                  ? ', selon les cumuls de précipitations mesurés à ' +
                    stationName
                  : ''}
                .
              </p>
            </ValueBox>
            {/* ====== 3. Tropical days ====== */}
            {showTropical && (
              <ValueBox color='var(--red-main)'>
                <h3>
                  <Anchor
                    onClick={() => {
                      askForScroll('tropicalAnchor') // ou 'hour'
                    }}
                  >
                    <WeatherSunny size={iconSize} />
                    {lang === 'fr'
                      ? tempFacts!.tropical_days.count > 0
                        ? tempFacts!.tropical_days.count > 1
                          ? `${tempFacts!.tropical_days.count} jours tropicaux`
                          : 'Un seul jour tropical'
                        : 'Jours tropicaux'
                      : 'Hitzetage'}
                  </Anchor>
                </h3>
                <p className='statement'>
                  {getSentence(
                    tempFacts!.tropical_days.count,
                    'tropicalDays',
                    lang,
                    tempFacts!.tropical_days.norm
                  )}
                </p>
                <p>
                  {lang === 'fr'
                    ? 'Cet été, ' +
                      dayReached(tempFacts!.tropical_days.count) +
                      ' atteint 30°C, la limite pour être qualifié de «tropical». La moyenne sur 30 ans est de ' +
                      prettifyNumber(tempFacts!.tropical_days.norm, 'fr') +
                      ` jours tropicaux jusqu’au ${getDate(
                        tempFacts!.current_ts.toString(),
                        'fr'
                      )}.`
                    : 'In diesem Sommer wurden bisher an ' +
                      tempFacts!.tropical_days.count +
                      (tempFacts!.tropical_days.count === 1
                        ? ' Tag '
                        : ' Tagen ') +
                      '30°C oder mehr erreicht. Bei diesen Werten spricht man von Hitzetagen. Diese Tage sind wegen des Klimawandels in den letzten Jahren tendenziell häufiger geworden.'}
                </p>
              </ValueBox>
            )}
            {/* ====== 4. Tropical nights ====== */}
            {showTropical && (
              <ValueBox color='var(--purple-main)'>
                <h3>
                  <Anchor
                    onClick={() => {
                      askForScroll('tropicalAnchor')
                    }}
                  >
                    <WeatherSunnyLow size={iconSize} />
                    {lang === 'fr'
                      ? tempFacts!.tropical_nights.count > 0
                        ? `${tempFacts!.tropical_nights.count} nuits tropicales`
                        : 'Nuits tropicales'
                      : 'Tropennächte'}
                  </Anchor>
                </h3>
                <p className='statement'>
                  {getSentence(
                    tempFacts!.tropical_nights.count,
                    'tropicalNights',
                    lang,
                    tempFacts!.tropical_nights.norm
                  )}
                </p>
                {tempFacts!.tropical_nights.count > 0 ? (
                  <p>
                    {lang === 'fr'
                      ? 'À ' +
                        tempFacts!.tropical_nights.count +
                        ' reprises cet été, la température n’est pas descendue sous 20 °C durant la nuit. La moyenne sur 30 ans est de ' +
                        prettifyNumber(tempFacts!.tropical_nights.norm, 'fr') +
                        ` nuits tropicales jusqu’au ${getDate(
                          tempFacts!.current_ts.toString(),
                          'fr'
                        )}.`
                      : 'An ' +
                        tempFacts!.tropical_nights.count +
                        (tempFacts!.tropical_nights.count === 1
                          ? ' Tag '
                          : ' Tagen ') +
                        'in diesem Sommer fielen die Temperaturen bisher nachts nicht unter 20°C. Dies wird als Tropennacht bezeichnet. Sie sind in der Schweiz immer noch selten, Tendenz zunehmend. In den Innenstädten sind sie wegen des Wärmeinsel-Effekts bereits heute häufig, wegen besseren Langzeitdaten bilden wir hier allerdings nur Werte von Wetterstationen ab, die sich nicht direkt in den Innenstädten befinden.'}
                  </p>
                ) : (
                  <p>
                    {lang === 'fr'
                      ? 'La température n’est pas encore restée au-dessus de la barre des vingt degrés cet été. On ne compte donc pas encore de nuit tropicale.'
                      : 'Bisher fielen in diesem Sommer die Temperaturen in jeder Nacht unter 20 °C. Damit kam es noch zu keinen Tropennächten.'}
                  </p>
                )}
              </ValueBox>
            )}
          </>
        )}
      </BoxContainer>
    </>
  )
}

const BoxContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
  row-gap: 10px;
  @media only screen and (max-width: 599px) {
    grid-template-columns: 1fr;
  }
`

const ValueBox = styled.div`
  border: 2px solid ${(props) => props.color};
  h3 {
    color: ${(props) => props.color};
  }
  .statement {
    font-weight: bold;
    font-size: 1.8rem;
  }
  p {
    font-size: 1.6rem;
  }
  border-radius: 10px;
  padding: 0 2rem;
  svg {
    margin-right: 0.5ch;
    fill: ${(props) => props.color};
  }
`

const Anchor = styled.span`
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
`

export default Facts
